import type {
  GetStaticPropsContext,
  InferGetStaticPropsType,
  NextPage,
} from 'next';

import { fetchFilteredCollectionsForHome } from 'utils/collection-filters.utils';
import { getCurrentISOString } from 'utils/date.utils';
import { filterValidFeaturedItems } from 'utils/featuredItems.utils';

import { Duration } from 'constants/duration.constants';
import { EVENTS_PER_PAGE_HOME } from 'constants/events.constant';
import { NOT_FOUND } from 'constants/nextjs.constants';
import { infiniteEventsQuery } from 'queries/events/queries';
import { pageHomeQuery } from 'queries/home/queries';
import { cityToCityCard } from 'queries/home/utils';
import { useHomePageView } from 'services/datalayer/browse';
import Prefetcher from 'services/prefetcher';

import HomeContainer from 'components/@home/HomeContainer';
import AppNavigation from 'components/@navigation/AppNavigation';
import PageSeo from 'components/@seo/PageSeo';

const Home: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  cities,
  collections,
  featuredItems,
  seoTags,
}) => {
  useHomePageView();

  return (
    <AppNavigation>
      <PageSeo tags={seoTags} />
      <HomeContainer
        cities={cities}
        featuredItems={featuredItems}
        collections={collections}
      />
    </AppNavigation>
  );
};
export default Home;

export const getStaticProps = async ({
  locale,
  preview,
}: GetStaticPropsContext) => {
  const prefetcher = await Prefetcher.init({ locale, preview });

  const currentISOString = getCurrentISOString();

  const [{ pageHome }] = await Promise.all([
    prefetcher.prefetch(pageHomeQuery, { currentISOString }),
    prefetcher.prefetchInfinite(
      infiniteEventsQuery,
      {
        filter: {
          endDatetime: { gte: currentISOString },
          unlistedEvent: { eq: false },
        },
        first: EVENTS_PER_PAGE_HOME,
        orderBy: 'startDatetime_ASC',
        skip: 0,
      },
      { meta: { dehydrate: true } },
    ),
  ]);

  if (!pageHome) return { ...NOT_FOUND };

  const cities = pageHome.cities?.map(cityToCityCard) ?? [];

  // collections may have time and venue filters to filter events
  const collections = await fetchFilteredCollectionsForHome(
    pageHome.collections,
    currentISOString,
    locale,
  );

  const featuredItems = filterValidFeaturedItems(pageHome.featuredItems ?? []);

  return {
    props: {
      cities,
      collections,
      featuredItems,
      id: pageHome.id,
      seoTags: pageHome.seoTags,
      dehydratedState: prefetcher.dehydratedState,
    },
    revalidate: Duration.TEN_MIN,
  };
};
